import {
  FULL_TIME_BETGROUP_NAME,
  LIVE_FULL_TIME_BET_MAP,
  MAIN_MARKETS_BY_SPORT,
} from 'config/constants';
import { Bet } from 'types/Bet';
import { SportIDs } from 'types/enums';
import {
  BasketballLiveEvent,
  LiveCountKey,
  LiveEvent,
  LiveEventBet,
  LiveEventOdd,
  TennisLiveEvent,
} from 'types/LiveEvent';
import { MatchOffer } from 'types/MatchOffer';

/** bet365 has suspended the pick for the moment (the board marks it
 *  "SU": 1): it shows a lock and cannot be picked until it reopens. */
const isSuspended = (x: { SU?: unknown }) => String(x?.SU ?? '') === '1';

function pickKickoffUnix(ev: Record<string, unknown>): number | undefined {
  const v = ev.kickoff_unix;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
    return Math.trunc(v);
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number.parseInt(v, 10);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return undefined;
}

const isZeroClock = (time: unknown) =>
  /^0?0:00$/.test(String(time ?? '').trim());

/** Kickoff as 24-hour local time ("20:45"), whatever the browser locale. */
const formatKickoffClock = (unix: number) => {
  const d = new Date(unix * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Bet365 lists an event a few minutes before it starts, with the clock at
 * 00:00; until kickoff the row shows the kickoff time. */
const upcomingKickoffLabel = (event: any): string | null => {
  const kickoffUnix = pickKickoffUnix(event);
  if (
    !isZeroClock(event.TIME) ||
    kickoffUnix === undefined ||
    kickoffUnix * 1000 <= Date.now()
  ) {
    return null;
  }
  return formatKickoffClock(kickoffUnix);
};

/** The feed lists a match minutes before kickoff; the live page shows only
 *  matches that have begun (owner, 2026-10-31). */
export const hasLiveEventStarted = (event: any): boolean =>
  upcomingKickoffLabel(event) === null;

// Volleyball, table tennis, badminton, beach volleyball: no match clock (TIME
// stays 00:00), so the row shows the set being played, as tennis does.
const SET_SPORTS = new Set([91, 92, 94, 95]);
const BASEBALL = 16;

/** What a live row shows instead of a clock that never runs. */
const clocklessLabel = (event: any, sportId: number): string | null => {
  if (!isZeroClock(event.TIME)) {
    return null;
  }
  if (SET_SPORTS.has(sportId)) {
    const sets = splitLiveScore(event.SS) ?? [];
    const played = sets.reduce((n, v) => n + (Number.parseInt(v, 10) || 0), 0);
    return `${played + 1}. Set`;
  }
  const ed = String(event.ED ?? '').trim();
  if (sportId === BASEBALL && ed) {
    return ed;
  }
  return 'Live';
};

export const mapSportEvents = (data: any[], sportId: SportIDs) => {
  return data.map((x) => extractFullTimeResult(x, sportId));
};

// Which side of a 1 / X / 2 an odd backs, and what kind of line it is. The
// feed stores lines markets two ways round: name "1" with header "Money Line"
// (basketball, baseball, hockey) or name "To Win" with header "1" (table
// tennis, handball, rugby). Plain two/three-way markets carry "1"/"2" (and
// "Draw" or "Tie") in the name or the header.
const oddSide = (value?: string | null): '1' | '2' | 'Draw' | '' => {
  const v = (value ?? '').trim();
  if (v === '1' || v === '2') return v;
  if (/^(tie|draw|x)\b/i.test(v)) return 'Draw';
  return '';
};

const WINNER_LINES = new Set(['', 'money line', 'to win', 'winner', 'result']);

/** The winner odds of a main market, renamed to "1", "Draw" and "2". */
export const winnerOdds = (bet: Bet) => {
  const out: NonNullable<Bet['odds']> = [];
  for (const odd of bet.odds ?? []) {
    if (odd.handicap || !(Number(odd.odds) > 0)) continue;
    let side = oddSide(odd.name);
    let line = odd.header ?? '';
    if (side !== '1' && side !== '2') {
      const fromHeader = oddSide(odd.header);
      if (fromHeader) {
        side = fromHeader;
        line = odd.name ?? '';
      }
    }
    if (!side || !WINNER_LINES.has(line.trim().toLowerCase())) continue;
    if (out.some((o) => o.name === side)) continue;
    out.push({ ...odd, name: side, header: '' });
  }
  return out;
};

const extractFullTimeResult = (
  match: MatchOffer,
  sportId: SportIDs
): MatchOffer => {
  const candidates = MAIN_MARKETS_BY_SPORT.get(Number(sportId)) ?? [];
  for (const marketName of candidates) {
    const market = match.bets.find((x: any) => x.name === marketName);
    if (!market) continue;
    const odds = winnerOdds(market);
    // A row needs both sides; a lone price is not a 1 / X / 2.
    if (
      !odds.some((o) => o.name === '1') ||
      !odds.some((o) => o.name === '2')
    ) {
      continue;
    }
    match.fullTimeBet = {
      name: FULL_TIME_BETGROUP_NAME,
      custom_id: market.custom_id,
      odds,
      priority: market.priority,
      realName: marketName,
    };
    // The picks shown in the row leave the market's own list; a market that
    // was nothing but them leaves the list altogether.
    const shown = new Set(odds.map((o) => o.custom_id));
    const rest = (market.odds ?? []).filter((o) => !shown.has(o.custom_id));
    match.bets =
      rest.length === 0
        ? match.bets.filter((x) => x !== market)
        : match.bets.map((x) => (x === market ? { ...x, odds: rest } : x));
    break;
  }

  match.bets.sort((x: Bet, y: Bet) => x.priority - y.priority);

  return match;
};

export const mapLiveEvent = (event: any, sportId: number): LiveEvent | null => {
  let mapResult = null;

  switch (sportId) {
    case SportIDs.SOCCER:
      mapResult = mapSoccerLiveEvent(event);
      break;
    case SportIDs.BASKETBALL:
      mapResult = mapBasketballLiveEvent(event);
      break;
    case SportIDs.TENNIS:
      mapResult = mapTennisLiveEvent(event);
      break;
    default:
      mapResult = mapGenericLiveEvent(event, sportId);
      break;
  }

  if (mapResult && sportId === SportIDs.SOCCER) {
    mapResult.halfTime = isHalfTime(event);
  }
  return mapResult;
};

/** The feed has no half-time state: the clock stops at exactly 45:00 (105:00
 *  in extra time). A half in play runs on through 45:01, its stoppage time. */
export const isHalfTimeClock = (time: unknown): boolean =>
  /^(45|105):00$/.test(String(time ?? '').trim());

/** Half time from bet365's clock flag TT ("1" runs, "0" stopped) when the
 *  feed sends it: a stopped clock between 45' and 90' (or at 105') is the
 *  break, a running one never is, even when it reads 45:00 at the restart
 *  (2026-11-04: "46'" then "PV" a second later, half time shown while play
 *  had restarted). Without TT, the clock text alone. */
export const isHalfTime = (event: {
  TT?: unknown;
  TIME?: unknown;
}): boolean => {
  const tt = String(event.TT ?? '').trim();
  if (tt !== '0' && tt !== '1') return isHalfTimeClock(event.TIME);
  if (tt === '1') return false;
  const minutes = Number.parseInt(String(event.TIME ?? '').split(':')[0], 10);
  if (!Number.isFinite(minutes)) return false;
  return (minutes >= 45 && minutes < 90) || minutes === 105;
};

/** What a live row's clock reads: the minute, or the break ("PV", "HZ"). */
export const liveClock = (
  event: { time: string | null; halfTime?: boolean },
  t: (key: string) => string
): string | null => (event.halfTime ? t('live_half_time_short') : event.time);

/** Match clock + period label (e.g. `03:12 (3rd)`) when Bet365 sends `ED`. */
const formatLiveTimeWithPeriod = (event: {
  TIME?: string;
  ED?: string;
}): string | null => {
  const clock = event.TIME;
  const rawEd = event.ED;
  if (rawEd === undefined || rawEd === null || String(rawEd).trim() === '') {
    return clock || null;
  }
  const edStr = String(rawEd).trim();
  const suffix = edStr.toLowerCase().includes('overtime')
    ? `OT ${edStr.split(/\s+/)[1] ?? ''}`.trim()
    : edStr.split(/\s+/)[0] || edStr;
  if (!suffix) {
    return clock || null;
  }
  if (!clock) {
    return `(${suffix})`;
  }
  return `${clock} (${suffix})`;
};

/**
 * When Bet365 sends no TU/ED (common for beach volleyball), use ES/MP/CP if present.
 * ES ~= current set (indoor volleyball). MP ~= elapsed minutes (volleyball / TT).
 * CP ~= phase code on some feeds (e.g. Q4 on basketball — usually covered by ED).
 */
const formatLiveTimeFallback = (
  event: { ES?: string | number; MP?: string | number; CP?: string },
  sportId: number
): string | null => {
  const esRaw = event.ES;
  if (sportId === 91 && esRaw !== undefined && esRaw !== null) {
    const esNum = Number.parseInt(String(esRaw).trim(), 10);
    if (!Number.isNaN(esNum) && esNum > 0) {
      return `Set ${esNum}`;
    }
  }
  const mpRaw = event.MP;
  if (mpRaw !== undefined && mpRaw !== null && String(mpRaw).trim() !== '') {
    const mpStr = String(mpRaw).trim();
    if (sportId === 91 || sportId === 92) {
      return `${mpStr}'`;
    }
  }
  const cpRaw = event.CP;
  if (cpRaw !== undefined && cpRaw !== null && String(cpRaw).trim() !== '') {
    return String(cpRaw).trim();
  }
  if (sportId === 95) {
    return 'Live';
  }
  return null;
};

const resolveLiveEventTimeDisplay = (
  event: {
    TIME?: string;
    ED?: string;
    ES?: string | number;
    MP?: string | number;
    CP?: string;
  },
  sportId: number
): string | null => {
  return (
    formatLiveTimeWithPeriod(event) ?? formatLiveTimeFallback(event, sportId)
  );
};

// "Result": rugby's "Game Lines 3-Way" names its winner picks so.
const LIVE_WINNER_LABEL =
  /^(winner|to win|money|money line|match winner|result)$/i;

const mapGenericLiveEvent = (event: any, sportId: number): LiveEvent | null => {
  const teams = splitLiveTeams(event.NA);
  const goals = splitLiveScore(event.SS);
  const fullTimeBet = event.BETS?.[0];

  if (!teams || !goals || !fullTimeBet) {
    return null;
  }

  // The winner picks: those without HA; failing that, the ones labelled as
  // such - HA "Winner" / "To Win" (volleyball, handball, table tennis) or NA
  // "Money" with the team in HA. The team may sit in NA, HA or the name.
  const isWinnerLabel = (v: unknown) =>
    typeof v === 'string' && LIVE_WINNER_LABEL.test(v.trim());
  const teamOf = (x: any) =>
    [x.NA, x.HA, x.name].find((v) => v === teams[0] || v === teams[1]);
  const allOdds: any[] = fullTimeBet.ODDS || [];
  const plainOdds = allOdds.filter((x: any) => !x.HA);
  const winnerOdds = plainOdds.length
    ? plainOdds
    : allOdds.filter((x: any) => isWinnerLabel(x.HA) || isWinnerLabel(x.NA));
  const fullTimeBetMapped = {
    name: FULL_TIME_BETGROUP_NAME,
    realName: fullTimeBet.NA,
    custom_id: '1',
    odds: winnerOdds.map((x: any) => {
      const team = teamOf(x);
      return {
        name: team === teams[0] ? '1' : team === teams[1] ? '2' : 'Draw',
        odds: x.OD,
        name2: x.N2,
        name3: x.name,
        custom_id: x.ID,
        suspended: isSuspended(x),
      };
    }),
  };

  return {
    home: { name: teams[0] },
    away: { name: teams[1] },
    homeGoals: Number.parseInt(goals[0]),
    awayGoals: Number.parseInt(goals[1]),
    time:
      upcomingKickoffLabel(event) ??
      clocklessLabel(event, sportId) ??
      resolveLiveEventTimeDisplay(event, sportId),
    fi: event.FI,
    league: event.CT,
    bets: (event.BETS || []).map((x: any) => mapLiveEventBet(x)),
    fullTimeBet: fullTimeBetMapped,
    sportId,
    kickoffUnix: pickKickoffUnix(event),
  };
};

const splitLiveTeams = (name: string): string[] | null => {
  if (!name) return null;

  if (name.includes(' v ')) return name.split(' v ');
  if (name.includes(' vs ')) return name.split(' vs ');
  if (name.includes(' @ ')) return name.split(' @ ').reverse();

  return null;
};

const splitLiveScore = (score: string): string[] | null => {
  if (!score || !score.includes('-')) return null;
  return score.split('-');
};

/** The scoreboard's red card column ("IRedCard"): home and away counts. */
const redCardsOf = (event: any) => {
  const stats = Array.isArray(event.STATS) ? event.STATS : [];
  const row = stats.find((s: any) => s?.NA === 'IRedCard');
  const n = (v: unknown) => Number.parseInt(String(v ?? ''), 10) || 0;
  return { homeRedCards: n(row?.HOME), awayRedCards: n(row?.AWAY) };
};

const LIVE_COUNT_COLUMNS: Record<string, LiveCountKey> = {
  IGoal: 'goals',
  ICorner: 'corners',
  IYellowCard: 'yellow',
  IRedCard: 'red',
  IThrowIn: 'throwins',
  IFreeKick: 'freekicks',
  IGoalKick: 'goalkicks',
  IPenalty: 'penalties',
  ISubstitution: 'substitutions',
};

/** The scoreboard's football counters; a column the feed leaves at "-" is
 *  left out. The live pitch animates what changes between updates. */
const liveCountsOf = (event: any): LiveEvent['liveCounts'] => {
  const stats = Array.isArray(event.STATS) ? event.STATS : [];
  const out: LiveEvent['liveCounts'] = {};
  stats.forEach((s: any) => {
    const key = LIVE_COUNT_COLUMNS[s?.NA];
    const h = Number.parseInt(String(s?.HOME ?? ''), 10);
    const a = Number.parseInt(String(s?.AWAY ?? ''), 10);
    if (key && Number.isFinite(h) && Number.isFinite(a)) out[key] = [h, a];
  });
  return out;
};

const mapSoccerLiveEvent = (event: any): LiveEvent | null => {
  const teams = event.NA.split(' v ');
  const goals = event.SS.split('-');
  const minute = Number.parseInt(event.TIME.split(':')[0]) + 1;
  const kickoffUnix = pickKickoffUnix(event);
  const time = upcomingKickoffLabel(event) ?? minute + "'";

  const fullTimeBet = event.BETS.filter(
    (x: any) => x.NA === LIVE_FULL_TIME_BET_MAP.get(SportIDs.SOCCER)
  )[0];

  const fullTimeBetMapped = {
    name: FULL_TIME_BETGROUP_NAME,
    realName: LIVE_FULL_TIME_BET_MAP.get(SportIDs.SOCCER),
    custom_id: '1',
    odds: fullTimeBet
      ? fullTimeBet.ODDS.map((x: any) => {
          return {
            name: x.NA === teams[0] ? '1' : x.NA === teams[1] ? '2' : 'Draw',
            odds: x.OD,
            custom_id: x.ID,
            suspended: isSuspended(x),
          };
        })
      : [],
  };

  return {
    home: {
      name: teams[0],
    },
    away: {
      name: teams[1],
    },
    homeGoals: Number.parseInt(goals[0]),
    awayGoals: Number.parseInt(goals[1]),
    time,
    fi: event.FI,
    league: event.CT,
    bets: event.BETS.map((x: any) => mapLiveEventBet(x)),
    fullTimeBet: fullTimeBetMapped,
    sportId: SportIDs.SOCCER,
    kickoffUnix,
    ...redCardsOf(event),
    liveCounts: liveCountsOf(event),
  };
};

const mapBasketballLiveEvent = (event: any): BasketballLiveEvent | null => {
  if (!event || typeof event.NA !== 'string' || !event.NA.trim()) {
    return null;
  }
  const switchTeams = event.NA.includes('@');
  let teams = event.NA.split(switchTeams ? ' @ ' : ' vs ');
  teams = switchTeams ? teams.reverse() : teams;
  if (!teams[0] || !teams[1]) {
    return null;
  }

  const ss = typeof event.SS === 'string' ? event.SS : '';
  if (!ss.includes('-')) {
    return null;
  }
  let goals = ss.split('-');
  if (switchTeams) {
    goals = goals.reverse();
  }

  const clock =
    event.TIME !== undefined && event.TIME !== null ? String(event.TIME) : '';
  const rawEd = event.ED;
  let suffix = '';
  if (rawEd !== undefined && rawEd !== null && String(rawEd).trim() !== '') {
    const edStr = String(rawEd).trim();
    suffix = edStr.toLowerCase().includes('overtime')
      ? `OT ${edStr.split(/\s+/)[1] ?? ''}`.trim()
      : edStr.split(/\s+/)[0] || edStr;
  }
  const timeDisplay =
    upcomingKickoffLabel(event) ??
    (clock && suffix ? `${clock} (${suffix})` : clock || suffix || 'Live');

  const bets = Array.isArray(event.BETS) ? event.BETS : [];
  const fullTimeBet = bets.filter(
    (x: any) => x.NA === LIVE_FULL_TIME_BET_MAP.get(SportIDs.BASKETBALL)
  )[0];

  const fullTimeBetMapped = {
    name: FULL_TIME_BETGROUP_NAME,
    realName: LIVE_FULL_TIME_BET_MAP.get(SportIDs.BASKETBALL),
    custom_id: '1',
    // The winner rows: "Money" with the team in HA (the feed's layout now),
    // or the team in NA with no HA (the old one).
    odds: fullTimeBet
      ? fullTimeBet.ODDS.filter(
          (x: any) => (x.NA === 'Money' && x.HA) || !x.HA
        ).map((x: any) => {
          const team = x.NA === 'Money' ? x.HA : x.NA;
          return {
            name: team === teams[0] ? '1' : team === teams[1] ? '2' : 'X',
            odds: x.OD,
            name2: x.N2,
            name3: x.name,
            custom_id: x.ID,
            suspended: isSuspended(x),
          };
        })
      : [],
  };

  const resultPerPeriod: string[] = [];
  const statNames = ['1', '2', '3', '4', 'OT'];
  let hasOt = false;

  const statsList = Array.isArray(event.STATS) ? event.STATS : [];
  statNames.forEach((x) => {
    const stats = statsList.find((y: any) => y.NA === x);
    if (stats && stats.HOME !== '' && stats.AWAY !== '') {
      resultPerPeriod.push(`${stats.HOME}:${stats.AWAY}`);

      if (stats.NA === 'OT' && stats.HOME !== '' && stats.AWAY !== '') {
        hasOt = true;
      }
    }
  });

  return {
    home: {
      name: teams[0],
    },
    away: {
      name: teams[1],
    },
    homeGoals: Number.parseInt(goals[0], 10),
    awayGoals: Number.parseInt(goals[1], 10),
    time: timeDisplay,
    fi: event.FI,
    league: event.CT,
    bets: bets.map((x: any) => mapLiveEventBet(x)),
    fullTimeBet: fullTimeBetMapped,
    quarter: rawEd !== undefined && rawEd !== null ? String(rawEd) : '',
    sportId: SportIDs.BASKETBALL,
    resultPerPeriod: resultPerPeriod,
    hasOvertime: hasOt,
    kickoffUnix: pickKickoffUnix(event),
  };
};

const mapTennisLiveEvent = (event: any): TennisLiveEvent | null => {
  const teams = event.NA.split(' v ');
  const result = event.SS.split(',');

  const fullTimeBet = event.BETS.filter(
    (x: any) => x.NA === LIVE_FULL_TIME_BET_MAP.get(SportIDs.TENNIS)
  )[0];

  const fullTimeBetMapped = {
    name: FULL_TIME_BETGROUP_NAME,
    realName: LIVE_FULL_TIME_BET_MAP.get(SportIDs.TENNIS),
    custom_id: '1',
    odds: fullTimeBet
      ? // "To Win" holds the match and the current set (HA "Match" /
        // "Set 2"); the row shows the match.
        fullTimeBet.ODDS.filter((x: any) => !x.HA || x.HA === 'Match').map(
          (x: any) => {
            return {
              name: x.NA === teams[0] ? '1' : x.NA === teams[1] ? '2' : 'X',
              name2: x.N2,
              name3: x.name,
              odds: x.OD,
              custom_id: x.ID,
              suspended: isSuspended(x),
            };
          }
        )
      : [],
  };

  const firstPlayerWonSets = getWonSetsNumber(result, 'first');
  const secondPlayerWonSets = getWonSetsNumber(result, 'second');
  const serveFromOdds = (() => {
    const allOdds = (event.BETS || []).flatMap((bet: any) => bet.ODDS || []);
    const serverOdd = allOdds.find(
      (odd: any) =>
        typeof odd?.NA === 'string' && odd.NA.toLowerCase().includes('(svr)')
    );
    if (!serverOdd || typeof serverOdd.NA !== 'string') return undefined;
    const oddName = serverOdd.NA.toLowerCase();
    if (oddName.includes(teams[0].toLowerCase())) return 'home';
    if (oddName.includes(teams[1].toLowerCase())) return 'away';
    return undefined;
  })();

  const time =
    upcomingKickoffLabel(event) ??
    firstPlayerWonSets + secondPlayerWonSets + 1 + '. Set';

  return {
    home: {
      name: teams[0],
    },
    away: {
      name: teams[1],
    },
    homeGoals: firstPlayerWonSets,
    awayGoals: secondPlayerWonSets,
    time: time,
    fi: event.FI,
    league: event.CT,
    bets: event.BETS.map((x: any) => mapLiveEventBet(x)),
    fullTimeBet: fullTimeBetMapped,
    currentSet: firstPlayerWonSets + secondPlayerWonSets + 1,
    sportId: SportIDs.TENNIS,
    kickoffUnix: pickKickoffUnix(event),
    resultPerPeriod: result.filter((x: string) => isSetFinished(x)),
    currentSetResult: result.findLast(() => true).replace('-', ':'),
    currentGameResult: (event.XP || '').replace('-', ':'),
    currentServe:
      String(event.SV_SIDE || '').trim() === 'home'
        ? 'home'
        : String(event.SV_SIDE || '').trim() === 'away'
          ? 'away'
          : String(event.SV || '').trim() === '1'
            ? 'home'
            : String(event.SV || '').trim() === '2'
              ? 'away'
              : serveFromOdds,
  };
};

export const splitEventsBySport = (data: any) => {
  const sportMap = new Map();

  data.forEach((e: any) => {
    if (sportMap.has(e.league.sport)) {
      sportMap.get(e.league.sport).push(e);
    } else {
      sportMap.set(e.league.sport, [e]);
    }
  });

  return sportMap;
};

export const splitLiveEventsBySport = (data: any) => {
  const sportMap = new Map();

  data.forEach((e: any) => {
    if (sportMap.has(e.results[0].sport_id)) {
      sportMap.get(e.results[0].sport_id).push(e);
    } else {
      sportMap.set(e.results[0].sport_id, [e]);
    }
  });

  return sportMap;
};

export const splitEventsByLeague = (data: any) => {
  const sportMap = new Map();

  data.forEach((e: any) => {
    if (sportMap.has(e.league.custom_id)) {
      sportMap.get(e.league.custom_id).push(e);
    } else {
      sportMap.set(e.league.custom_id, [e]);
    }
  });

  return sportMap;
};

const getWonSetsNumber = (
  results: string[],
  player: 'first' | 'second'
): number => {
  return results.filter((setResult: string) => {
    const games = setResult.split('-');
    const targetPlayerResult = Number.parseInt(
      games[player === 'first' ? 0 : 1]
    );
    const oponentResult = Number.parseInt(games[player === 'first' ? 1 : 0]);

    let targetPlayerWon = false;

    if (
      (targetPlayerResult === 6 && oponentResult < 5) ||
      (targetPlayerResult === 7 && oponentResult <= 6)
    ) {
      targetPlayerWon = true;
    }

    return targetPlayerWon;
  }).length;
};

const isSetFinished = (setResult: string): boolean => {
  const games = setResult.split('-');
  const firstPlayerResult = Number.parseInt(games[0]);
  const secondPlayerResult = Number.parseInt(games[1]);

  return (
    (firstPlayerResult === 6 && secondPlayerResult < 5) ||
    (firstPlayerResult === 7 && secondPlayerResult <= 6) ||
    (secondPlayerResult === 6 && firstPlayerResult < 5) ||
    (secondPlayerResult === 7 && firstPlayerResult <= 6)
  );
};

const mapLiveEventBet = (bet: any): LiveEventBet => {
  return {
    name: bet.NA,
    odds: bet.ODDS.map((x: any) => mapLiveEventOdd(x)),
  };
};

const mapLiveEventOdd = (odd: any): LiveEventOdd => {
  return {
    handicap: odd.HA,
    header: odd.HD,
    name: odd.NA,
    name2: odd.N2,
    odds: odd.OD,
    custom_id: odd.ID,
    suspended: isSuspended(odd),
    name3: odd.name,
  };
};
